"use client";
import React, { useState, useEffect } from "react";
import Link from "next/link";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

const heroSlides = [
{
  image: "https://images.unsplash.com/photo-1728565721620-fefb198160be",
  alt: "Mevlana Müzesi, Konya — historic green-domed mausoleum exterior, warm golden afternoon light, deep blue sky, atmospheric grandeur",
  label: "Mevlana Müzesi"
},
{
  image: "https://images.unsplash.com/photo-1695623024728-fe04fdd5f836",
  alt: "Karatay Medresesi çini kubbeleri — ornate turquoise tile dome interior, intricate Islamic geometric patterns, dim atmospheric lighting, shadowed stone walls",
  label: "Karatay Medresesi"
},
{
  image: "https://images.unsplash.com/photo-1627137117441-8cf09d8e803d",
  alt: "Konya şehir merkezi tarihi çarşı — stone archways, cobblestone street, warm amber shop lights, evening dusk atmosphere, deep shadows",
  label: "Tarihi Bedesten"
},
{
  image: "https://images.unsplash.com/photo-1733997345433-ddda43d6a58c",
  alt: "Alaaddin Tepesi panorama — elevated hilltop mosque silhouette against sunset sky, city sprawl below, deep orange and purple dusk tones",
  label: "Alaaddin Tepesi"
}];


const QUICK_CATS = [
{ emoji: "🍽️", label: "Restoran" },
{ emoji: "☕", label: "Kafe" },
{ emoji: "🎭", label: "Etkinlik" },
{ emoji: "🛍️", label: "Alışveriş" },
{ emoji: "🏥", label: "Sağlık" },
{ emoji: "📚", label: "Eğitim" },
{ emoji: "🔧", label: "Hizmetler" },
{ emoji: "🏨", label: "Konaklama" }];


export default function HeroSection() {
  const [activeSlide, setActiveSlide] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [district, setDistrict] = useState("Tüm İlçeler");

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % heroSlides?.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      {/* Background Images */}
      {heroSlides?.map((slide, i) =>
      <div
        key={i}
        className="absolute inset-0 transition-opacity duration-1000"
        style={{ opacity: i === activeSlide ? 1 : 0 }}>
        
          <AppImage
          src={slide?.image}
          alt={slide?.alt}
          fill
          className="object-cover"
          priority={i === 0}
          sizes="100vw" />
        
        </div>
      )}

      {/* Gradient Overlay — dark from bottom for white text legibility */}
      <div className="absolute inset-0 gradient-hero z-10" />

      {/* Seljuk pattern subtle overlay */}
      <div className="absolute inset-0 z-10 seljuk-pattern opacity-30" />

      {/* Slide indicators */}
      <div className="absolute top-1/2 right-6 -translate-y-1/2 z-20 flex flex-col gap-2">
        {heroSlides?.map((slide, i) =>
        <button
          key={i}
          onClick={() => setActiveSlide(i)}
          aria-label={slide?.label}
          className={`w-1.5 rounded-full transition-all duration-300 ${
          i === activeSlide ? "h-8 bg-white" : "h-3 bg-white/40"}`
          } />

        )}
      </div>

      {/* Current slide label */}
      <div className="absolute top-32 left-6 md:left-10 z-20">
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold text-white bg-white/15 backdrop-blur-sm border border-white/20">
          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
          {heroSlides?.[activeSlide]?.label}
        </span>
      </div>

      {/* Hero Content */}
      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 pb-16 pt-40 w-full">
        {/* Eyebrow */}
        <div className="fade-in-up mb-4">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.3em] text-white/80">
            <span className="w-8 h-px bg-white/60" />
            Kültür · Yaşam · Tanıtım
          </span>
        </div>

        {/* Headline */}
        <h1 className="text-hero-xl text-white mb-4 fade-in-up delay-100 max-w-3xl">
          Konya&apos;yı Keşfet,
          <br />
          <span style={{ color: "#F9A8D4" }} className="text-[rgba(0,180,180,1)]">Fırsatları</span> Yakala
        </h1>

        <p className="text-white/80 text-base md:text-lg mb-8 fade-in-up delay-200 max-w-xl font-light">
          Konya&apos;nın Şehir Fırsatları – Kültür | Yaşam | Yerel Hizmetler
        </p>

        {/* Search Bar */}
        <div className="fade-in-up delay-300 max-w-2xl mb-6">
          <div className="flex flex-col sm:flex-row gap-2 p-2 rounded-2xl bg-white/15 backdrop-blur-md border border-white/20">
            <div className="flex-1 flex items-center gap-3 bg-white rounded-xl px-4 py-3">
              <Icon name="MagnifyingGlassIcon" size={20} className="text-muted-foreground shrink-0" />
              <input
                type="text"
                placeholder="Firma, mekan, etkinlik ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="flex-1 text-sm text-foreground placeholder-muted-foreground bg-transparent outline-none" />
              
            </div>
            <select
              value={district}
              onChange={(e) => setDistrict(e?.target?.value)}
              className="bg-white rounded-xl px-4 py-3 text-sm text-foreground outline-none cursor-pointer border-0">
              
              <option>Tüm İlçeler</option>
              <option>Selçuklu</option>
              <option>Meram</option>
              <option>Karatay</option>
            </select>
            <Link
              href="/directory"
              className="btn-primary rounded-xl whitespace-nowrap">
              
              <Icon name="MagnifyingGlassIcon" size={16} />
              Ara
            </Link>
          </div>
        </div>

        {/* Category Chips */}
        <div className="fade-in-up delay-400 flex flex-wrap gap-2">
          {QUICK_CATS?.map((cat) =>
          <Link
            key={cat?.label}
            href={`/directory`}
            className="category-chip">
            
              <span>{cat?.emoji}</span>
              <span>{cat?.label}</span>
            </Link>
          )}
        </div>
      </div>

      {/* Mobile Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-50 sm:hidden bg-white border-t border-border px-4 py-3 flex gap-2">
        <a
          href="tel:+903320000000"
          className="flex-1 flex flex-col items-center gap-1 py-1 text-muted-foreground hover:text-primary transition-colors">
          
          <Icon name="PhoneIcon" size={20} />
          <span className="text-xs font-medium">Ara</span>
        </a>
        <a
          href="https://wa.me/905320000000"
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 flex flex-col items-center gap-1 py-1 text-muted-foreground hover:text-primary transition-colors">
          
          <Icon name="ChatBubbleLeftRightIcon" size={20} />
          <span className="text-xs font-medium">WhatsApp</span>
        </a>
        <a
          href="https://maps.google.com/?q=Konya"
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 flex flex-col items-center gap-1 py-1 text-muted-foreground hover:text-primary transition-colors">
          
          <Icon name="MapPinIcon" size={20} />
          <span className="text-xs font-medium">Yol Tarifi</span>
        </a>
      </div>
    </section>);

}